import React, { useEffect, useState } from "react";
import {
    DollarSign, Package, Users, Crown, Wallet, Gift, TrendingUp, ShoppingBag,
    AlertTriangle, Tag, RefreshCw
} from "lucide-react";
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    BarChart, Bar, Area, AreaChart
} from "recharts";
import { toast } from "sonner";
import api from "../lib/api";

const CHART_TEXT_STYLE = { fill: "#A8998B", fontSize: 11, fontFamily: "ui-monospace, monospace" };

export const AdminAnalyticsPanel = ({ token }) => {
    const headers = { "X-Admin-Token": token };
    const [data, setData] = useState(null);
    const [revenueSeries, setRevenueSeries] = useState([]);
    const [growthSeries, setGrowthSeries] = useState([]);
    const [topProducts, setTopProducts] = useState([]);
    const [lowStock, setLowStock] = useState([]);
    const [topCodes, setTopCodes] = useState([]);
    const [forecast, setForecast] = useState(null);
    const [loading, setLoading] = useState(false);
    const [range, setRange] = useState(30);

    const load = async () => {
        setLoading(true);
        try {
            const [overview, rev, growth, top, low, codes, fc] = await Promise.all([
                api.get("/admin/analytics/overview", { headers }),
                api.get(`/admin/analytics/revenue?days=${range}`, { headers }),
                api.get(`/admin/analytics/customer-growth?days=${range}`, { headers }),
                api.get("/admin/analytics/top-products?limit=8", { headers }),
                api.get("/admin/analytics/low-stock?threshold=10&limit=6", { headers }),
                api.get("/admin/analytics/top-codes?limit=5", { headers }),
                api.get("/admin/analytics/forecast", { headers }),
            ]);
            setData(overview.data);
            setRevenueSeries(rev.data.series);
            setGrowthSeries(growth.data.series);
            setTopProducts(top.data.products);
            setLowStock(low.data.products);
            setTopCodes(codes.data.codes);
            setForecast(fc.data);
        } catch {
            toast.error("Could not load analytics");
        }
        setLoading(false);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [range]);

    if (loading && !data) {
        return <div className="text-center text-[var(--text-secondary)] py-12 text-sm">Loading analytics…</div>;
    }
    if (!data) return null;

    return (
        <div className="space-y-5" data-testid="admin-analytics-panel">
            {/* Hero stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <HeroStat icon={DollarSign} label="Today" value={`A$${data.revenue.today.toLocaleString()}`} sub={`${data.orders.today} orders`} highlight />
                <HeroStat icon={DollarSign} label="This week" value={`A$${data.revenue.week.toLocaleString()}`} sub={`${data.orders.week} orders`} />
                <HeroStat icon={DollarSign} label="This month" value={`A$${data.revenue.month.toLocaleString()}`} sub={`${data.orders.month} orders`} />
                <HeroStat icon={DollarSign} label="This year" value={`A$${data.revenue.year.toLocaleString()}`} sub={`${data.orders.year} orders`} />
            </div>

            {/* Secondary stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <SmallStat icon={Package} label="Awaiting fulfilment" value={data.orders.awaiting_fulfilment} />
                <SmallStat icon={ShoppingBag} label="Shipped" value={data.orders.shipped} />
                <SmallStat icon={TrendingUp} label="Avg order value" value={`A$${data.average_order_value.toFixed(2)}`} />
                <SmallStat icon={Users} label="Returning customer" value={`${data.returning_customer_rate_pct}%`} />
                <SmallStat icon={Crown} label="Active VIP" value={data.customers.active_vips} />
                <SmallStat icon={Users} label="Customers" value={data.customers.total} />
                <SmallStat icon={Wallet} label="Credit outstanding" value={`A$${data.store_credit_outstanding.toFixed(2)}`} />
                <SmallStat icon={Gift} label="Voucher balance" value={`A$${data.voucher_balance_outstanding.toFixed(2)}`} />
            </div>

            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    {[7, 30, 90, 365].map((d) => (
                        <button
                            key={d}
                            onClick={() => setRange(d)}
                            className={`px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-widest border ${range === d ? "border-[var(--gold)] text-[var(--gold)] bg-[rgba(233,188,11,0.08)]" : "border-white/10 text-[var(--text-secondary)] hover:border-white/30"}`}
                            data-testid={`analytics-range-${d}`}
                        >
                            {d === 365 ? "1y" : `${d}d`}
                        </button>
                    ))}
                </div>
                <button onClick={load} className="px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-widest border border-white/10 text-[var(--text-secondary)] hover:text-white hover:border-white/30 inline-flex items-center gap-1" data-testid="analytics-refresh">
                    <RefreshCw size={12} /> Refresh
                </button>
            </div>

            {/* Revenue chart */}
            <div className="grid lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 glass-elevated rounded-2xl p-5" data-testid="revenue-chart">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">Revenue</p>
                        <h3 className="font-display text-xl font-extrabold text-white italic tracking-tighter">Last {range} days</h3>
                    </div>
                </div>
                <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={revenueSeries} margin={{ top: 10, right: 12, left: -10, bottom: 0 }}>
                            <defs>
                                <linearGradient id="gold-fill" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#E9BC0B" stopOpacity={0.45} />
                                    <stop offset="100%" stopColor="#E9BC0B" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid stroke="rgba(255,255,255,0.04)" />
                            <XAxis dataKey="date" tick={CHART_TEXT_STYLE} stroke="rgba(255,255,255,0.1)" tickFormatter={(d) => d.slice(5)} />
                            <YAxis tick={CHART_TEXT_STYLE} stroke="rgba(255,255,255,0.1)" />
                            <Tooltip
                                contentStyle={{ background: "#0F1115", border: "1px solid rgba(233,188,11,0.4)", borderRadius: 8, color: "#fff", fontSize: 12 }}
                                formatter={(v) => [`A$${Number(v).toFixed(2)}`, "Revenue"]}
                            />
                            <Area type="monotone" dataKey="revenue" stroke="#E9BC0B" strokeWidth={2} fill="url(#gold-fill)" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
                </div>
                {forecast && <ForecastCard forecast={forecast} />}
            </div>

            {/* Customer growth */}
            <div className="grid lg:grid-cols-2 gap-4">
                <div className="glass-elevated rounded-2xl p-5" data-testid="growth-chart">
                    <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">Customer growth</p>
                    <h3 className="font-display text-xl font-extrabold text-white italic tracking-tighter mb-4">New signups</h3>
                    <div className="h-48">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={growthSeries} margin={{ top: 10, right: 12, left: -10, bottom: 0 }}>
                                <CartesianGrid stroke="rgba(255,255,255,0.04)" />
                                <XAxis dataKey="date" tick={CHART_TEXT_STYLE} stroke="rgba(255,255,255,0.1)" tickFormatter={(d) => d.slice(5)} />
                                <YAxis tick={CHART_TEXT_STYLE} stroke="rgba(255,255,255,0.1)" allowDecimals={false} />
                                <Tooltip contentStyle={{ background: "#0F1115", border: "1px solid rgba(233,188,11,0.4)", borderRadius: 8, fontSize: 12 }} />
                                <Bar dataKey="new_customers" fill="#E9BC0B" radius={[3, 3, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="glass-elevated rounded-2xl p-5" data-testid="top-products">
                    <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">Best sellers</p>
                    <h3 className="font-display text-xl font-extrabold text-white italic tracking-tighter mb-4">Top products</h3>
                    {topProducts.length === 0 && <p className="text-sm text-[var(--text-secondary)]">No sales yet.</p>}
                    <div className="space-y-2">
                        {topProducts.map((p, i) => (
                            <div key={p.product_slug} className="flex items-center justify-between gap-3 p-2 rounded-lg bg-black/30 border border-white/5" data-testid={`top-product-${p.product_slug}`}>
                                <div className="flex items-center gap-3 min-w-0">
                                    <span className="font-mono text-[10px] text-[var(--text-muted)] w-5">#{i + 1}</span>
                                    {p.image && <img src={p.image} alt="" className="w-14 h-9 rounded object-contain" />}
                                    <div className="min-w-0">
                                        <p className="text-sm text-white truncate">{p.product_name}</p>
                                        <p className="text-[10px] text-[var(--text-muted)] font-mono">{p.quantity_sold} sold · {p.appears_in_orders} orders</p>
                                    </div>
                                </div>
                                <p className="text-sm font-mono text-[var(--gold)] font-bold whitespace-nowrap">A${Number(p.revenue_estimate).toFixed(0)}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Low stock + top codes */}
            <div className="grid lg:grid-cols-2 gap-4">
                <div className="glass-elevated rounded-2xl p-5" data-testid="low-stock">
                    <p className="text-[10px] font-mono uppercase tracking-widest text-amber-300 inline-flex items-center gap-1.5">
                        <AlertTriangle size={11} /> Low stock alert
                    </p>
                    <h3 className="font-display text-xl font-extrabold text-white italic tracking-tighter mb-4">Restock soon</h3>
                    {lowStock.length === 0 && <p className="text-sm text-emerald-300">All products well stocked.</p>}
                    <div className="space-y-2">
                        {lowStock.map((p) => (
                            <div key={p.slug} className="flex items-center justify-between gap-3 p-2 rounded-lg bg-amber-500/5 border border-amber-500/20" data-testid={`low-stock-${p.slug}`}>
                                <div className="flex items-center gap-3 min-w-0">
                                    {p.image && <img src={p.image} alt="" className="w-14 h-9 rounded object-contain" />}
                                    <div className="min-w-0">
                                        <p className="text-sm text-white truncate">{p.name}</p>
                                        <p className="text-[10px] text-[var(--text-muted)] font-mono">A${p.price}</p>
                                    </div>
                                </div>
                                <p className="text-sm font-mono text-amber-300 font-bold">{p.stock} left</p>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="glass-elevated rounded-2xl p-5" data-testid="top-codes">
                    <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] inline-flex items-center gap-1.5">
                        <Tag size={11} /> Most used codes
                    </p>
                    <h3 className="font-display text-xl font-extrabold text-white italic tracking-tighter mb-4">Promo activity</h3>
                    {topCodes.length === 0 && <p className="text-sm text-[var(--text-secondary)]">No codes redeemed yet.</p>}
                    <div className="space-y-2">
                        {topCodes.map((c) => (
                            <div key={c.code} className="flex items-center justify-between gap-3 p-2 rounded-lg bg-black/30 border border-white/5">
                                <div>
                                    <p className="font-mono text-sm text-white tracking-widest font-bold">{c.code}</p>
                                    <p className="text-[10px] text-[var(--text-muted)]">
                                        {c.type === "percentage" ? `${c.value}% off` : `A$${c.value} off`}
                                        {c.usage_limit ? ` · ${c.usage_count}/${c.usage_limit}` : ` · ${c.usage_count} used`}
                                    </p>
                                </div>
                                <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full border ${c.is_active ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" : "text-[var(--text-muted)] border-white/10"}`}>
                                    {c.is_active ? "active" : "off"}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

const HeroStat = ({ icon: Icon, label, value, sub, highlight = false }) => (
    <div className={`glass-elevated rounded-2xl p-5 relative overflow-hidden ${highlight ? "border border-[var(--gold)]/30" : ""}`}>
        {highlight && (
            <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full opacity-30"
                 style={{ background: "radial-gradient(circle, rgba(233,188,11,0.5), transparent 70%)" }} />
        )}
        <div className="relative">
            <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-2 inline-flex items-center gap-1.5">
                <Icon size={12} /> {label}
            </p>
            <p className={`font-display text-3xl font-extrabold italic tracking-tighter ${highlight ? "text-[var(--gold)]" : "text-white"}`}>{value}</p>
            <p className="text-[10px] text-[var(--text-muted)] font-mono mt-1">{sub}</p>
        </div>
    </div>
);

const SmallStat = ({ icon: Icon, label, value }) => (
    <div className="glass rounded-xl p-3">
        <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1 inline-flex items-center gap-1.5">
            <Icon size={11} /> {label}
        </p>
        <p className="font-mono text-white text-lg font-bold">{value}</p>
    </div>
);

const BADGE_STYLES = {
    ahead: { label: "Ahead of last month", text: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-500/40" },
    on_track: { label: "On track", text: "text-[var(--gold)]", bg: "bg-[rgba(233,188,11,0.06)]", border: "border-[var(--gold)]/40" },
    behind: { label: "Behind last month", text: "text-red-300", bg: "bg-red-500/10", border: "border-red-500/40" },
    no_baseline: { label: "First month", text: "text-[var(--text-secondary)]", bg: "bg-white/5", border: "border-white/10" },
};

const ForecastCard = ({ forecast }) => {
    const badge = BADGE_STYLES[forecast.badge] || BADGE_STYLES.no_baseline;
    const ratioPct = forecast.vs_previous_pct;
    return (
        <div className="glass-elevated rounded-2xl p-5 relative overflow-hidden" data-testid="revenue-forecast">
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-20"
                 style={{ background: "radial-gradient(circle, rgba(233,188,11,0.5), transparent 70%)" }} />
            <div className="relative">
                <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">{forecast.month_label} forecast</p>
                <p className="font-display text-3xl font-extrabold italic tracking-tighter text-[var(--gold)] mb-2">
                    A${Number(forecast.projected_total).toLocaleString()}
                </p>
                <span className={`inline-block text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full border ${badge.text} ${badge.bg} ${badge.border}`}
                      data-testid={`forecast-badge-${forecast.badge}`}>
                    {badge.label}{ratioPct !== null ? ` · ${ratioPct >= 0 ? "+" : ""}${ratioPct}%` : ""}
                </span>
                <div className="mt-5 space-y-2">
                    <ForecastRow label="So far" value={`A$${Number(forecast.revenue_so_far).toLocaleString()}`} />
                    <ForecastRow label="Daily run rate" value={`A$${Number(forecast.daily_run_rate).toFixed(0)}`} />
                    <ForecastRow label={`${forecast.previous_month_label} total`} value={`A$${Number(forecast.previous_month_revenue).toLocaleString()}`} />
                    <ForecastRow label="Days remaining" value={`${forecast.days_remaining} of ${forecast.days_in_month}`} />
                </div>
                <div className="mt-4 h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full rounded-full transition-all"
                         style={{
                             width: `${Math.min(100, (forecast.days_elapsed / forecast.days_in_month) * 100)}%`,
                             background: "linear-gradient(90deg, rgba(233,188,11,0.4), var(--gold))",
                         }} />
                </div>
            </div>
        </div>
    );
};

const ForecastRow = ({ label, value }) => (
    <div className="flex items-center justify-between text-xs">
        <span className="text-[var(--text-muted)] font-mono uppercase tracking-widest">{label}</span>
        <span className="text-white font-mono font-bold">{value}</span>
    </div>
);
